import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Ship, Anchor, Sailboat, Package } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const services = [
  {
    id: '01',
    key: 'portAgency',
    title_en: 'Port Agency',
    title_ru: 'Портовое агентство',
    desc_en: 'Full-scope port agency operations across all Turkish ports. We handle customs clearance, vessel operations, crew changes, cargo documentation, and coordination with local authorities.',
    desc_ru: 'Полный спектр портовых агентских операций во всех турецких портах. Таможенное оформление, судовые операции, смена экипажа.',
    features_en: ['Customs Clearance', 'Crew Changes', 'Cargo Documentation', 'Local Authority Coordination', 'Vessel Operations'],
    features_ru: ['Таможенное оформление', 'Смена экипажа', 'Документация груза', 'Координация с властями', 'Судовые операции'],
    image: 'https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/95o1vrse_aerial-view-of-large-general-cargo-ship-tanker-bul-2023-11-27-05-02-09-utc-scaled.jpg',
    icon: Anchor,
  },
  {
    id: '02',
    key: 'cruiseAgency',
    title_en: 'Cruise Agency',
    title_ru: 'Круизное агентство',
    desc_en: 'Specialized cruise ship handling with comprehensive passenger logistics, shore excursions coordination, and VIP services at Turkish cruise destinations.',
    desc_ru: 'Специализированное обслуживание круизных судов с комплексной логистикой пассажиров.',
    features_en: ['Passenger Logistics', 'Shore Excursions', 'VIP Services', 'Port Coordination', 'Gangway Operations'],
    features_ru: ['Логистика пассажиров', 'Береговые экскурсии', 'VIP услуги', 'Координация порта', 'Операции трапа'],
    image: 'https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/fu70ioar_cruise-ship-cruise-liners-beautiful-white-cruise-2024-12-08-00-31-58-utc-scaled.jpg',
    icon: Ship,
  },
  {
    id: '03',
    key: 'yachtAgency',
    title_en: 'Yacht Agency',
    title_ru: 'Яхтенное агентство',
    desc_en: 'Premium yacht services including berthing arrangements, provisioning, concierge, and technical support for luxury vessels along the Turkish coast.',
    desc_ru: 'Премиальные яхтенные услуги включая организацию стоянки, снабжение, консьерж и техническую поддержку.',
    features_en: ['Berthing Arrangements', 'Provisioning', 'Concierge Services', 'Technical Support', 'Crew Services'],
    features_ru: ['Организация стоянки', 'Снабжение', 'Консьерж услуги', 'Техническая поддержка', 'Услуги экипажу'],
    image: 'https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/lmdb61r2_luxury-yacht-and-blue-sea-at-sunset-in-summer-aer-2024-11-26-02-27-18-utc-scaled.jpg',
    icon: Sailboat,
  },
  {
    id: '04',
    key: 'shipSupply',
    title_en: 'Ship Supply',
    title_ru: 'Судовое снабжение',
    desc_en: 'Complete ship chandlery services including technical stores, provisions, spare parts delivery, and marine equipment supply.',
    desc_ru: 'Полное судовое снабжение включая технические запасы, провизию, запасные части.',
    features_en: ['Technical Stores', 'Provisions', 'Spare Parts', 'Marine Equipment', 'Fast Delivery'],
    features_ru: ['Технические запасы', 'Провизия', 'Запасные части', 'Морское оборудование', 'Быстрая доставка'],
    image: 'https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/dg6dn9wz_various-food-ingredients-2024-10-22-10-40-13-utc-scaled.jpg',
    icon: Package,
  },
];

export const Services = () => {
  const { language } = useLanguage();
  const [activeService, setActiveService] = useState(0);

  return (
    <main data-testid="services-page" className="min-h-screen pt-24">
      {/* Header with Video Background */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/fu70ioar_cruise-ship-cruise-liners-beautiful-white-cruise-2024-12-08-00-31-58-utc-scaled.jpg"
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src="https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/dz1qqosg_DJI_0868.MP4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-navy/60 z-10" />
        
        <div className="container-bespoke relative z-20">
          <p className="text-editorial text-white/70 mb-6">
            {language === 'en' ? 'What We Do' : 'Что мы делаем'}
          </p>
          <h1 className="font-heading text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-8 drop-shadow-lg">
            {language === 'en' ? 'Our Services' : 'Наши услуги'}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            {language === 'en'
              ? 'Comprehensive maritime solutions tailored to your vessel\'s needs at every Turkish port.'
              : 'Комплексные морские решения для ваших судов во всех турецких портах.'}
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container-bespoke">
          {services.map((service, index) => (
            <div
              key={service.key}
              data-testid={`service-section-${service.key}`}
              className={`py-16 lg:py-24 ${index !== services.length - 1 ? 'border-b border-slate-200' : ''}`}
            >
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-mono text-sm text-slate-400">{service.id}</span>
                    <service.icon className="w-6 h-6 text-signal" />
                  </div>
                  <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-navy mb-6">
                    {language === 'en' ? service.title_en : service.title_ru}
                  </h2>
                  <p className="text-slate-600 text-lg leading-relaxed mb-8">
                    {language === 'en' ? service.desc_en : service.desc_ru}
                  </p>
                  
                  {/* Features */}
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {(language === 'en' ? service.features_en : service.features_ru).map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-1 h-1 bg-signal rounded-full" />
                        <span className="text-sm text-slate-600">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link to="/contact">
                    <button className="btn-outline">
                      {language === 'en' ? 'Request Quote' : 'Запросить'}
                      <ArrowRight className="inline ml-2 w-4 h-4" />
                    </button>
                  </Link>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img
                    src={service.image}
                    alt={service.title_en}
                    className="w-full h-[400px] lg:h-[500px] object-cover object-center rounded-[20px]"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-navy">
        <div className="container-bespoke text-center">
          <p className="text-editorial text-slate-400 mb-6">
            {language === 'en' ? 'Need a custom solution?' : 'Нужно индивидуальное решение?'}
          </p>
          <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-white mb-8">
            {language === 'en' ? 'Let\'s discuss your requirements' : 'Давайте обсудим ваши требования'}
          </h2>
          <Link to="/contact">
            <button className="btn-signal">
              {language === 'en' ? 'Get in Touch' : 'Связаться'}
              <ArrowRight className="inline ml-2 w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </main>
  );
};
